/**
 * Deep URL diagnostics and monitoring utilities
 */

import logger from '@/utils/logger';

export interface DeepUrlState {
  // Multiple URL sources
  windowLocationHref: string;
  windowLocationSearch: string;
  windowLocationPathname: string;
  documentLocationHref: string;
  documentLocationSearch: string;

  // History API
  historyLength: number;
  historyState: unknown;

  // URLSearchParams attempts
  urlSearchParamsFromWindow: URLSearchParams;
  urlSearchParamsFromDocument: URLSearchParams;

  // Manual parameter extraction
  manualParamsFromHref: Record<string, string>;
  manualParamsFromSearch: Record<string, string>;

  // React Router (if available)
  reactRouterLocation?: unknown;

  // Timing
  timestamp: Date;
  userAgent: string;
}

/**
 * Extract parameters manually from any URL string
 */
export const extractParametersManually = (url: string): Record<string, string> => {
  const params: Record<string, string> = {};

  try {
    // Find the ? character
    const questionIndex = url.indexOf('?');
    if (questionIndex === -1) return params;

    // Get everything after the ?
    const queryString = url.substring(questionIndex + 1);

    // Split by & and process each pair
    const pairs = queryString.split('&');
    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key && value) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    }
  } catch (error) {
    console.error('🚨 Manual parameter extraction failed:', error);
  }

  return params;
};

/**
 * Get comprehensive URL state from all possible sources
 */
export const getDeepUrlState = (reactRouterLocation?: unknown): DeepUrlState => {
  const timestamp = new Date();

  // Window location
  const windowLocationHref = window.location.href;
  const windowLocationSearch = window.location.search;
  const windowLocationPathname = window.location.pathname;

  // Document location (sometimes different)
  const documentLocationHref = document.location.href;
  const documentLocationSearch = document.location.search;

  // History
  const historyLength = window.history.length;
  const historyState = window.history.state;

  // URLSearchParams from different sources
  const urlSearchParamsFromWindow = new URLSearchParams(windowLocationSearch);
  const urlSearchParamsFromDocument = new URLSearchParams(documentLocationSearch);

  // Manual extraction
  const manualParamsFromHref = extractParametersManually(windowLocationHref);
  const manualParamsFromSearch = extractParametersManually(windowLocationSearch);

  return {
    windowLocationHref,
    windowLocationSearch,
    windowLocationPathname,
    documentLocationHref,
    documentLocationSearch,
    historyLength,
    historyState,
    urlSearchParamsFromWindow,
    urlSearchParamsFromDocument,
    manualParamsFromHref,
    manualParamsFromSearch,
    reactRouterLocation,
    timestamp,
    userAgent: navigator.userAgent
  };
};

/**
 * Find sessionId and playerId from any available source
 */
export const findParametersFromAllSources = (deepState: DeepUrlState): {
  sessionId: string | null;
  playerId: string | null;
  gameCode: string | null;
  source: string;
} => {
  // Try different sources in order of reliability
  const sources = [
    { name: 'manual-href', params: deepState.manualParamsFromHref },
    { name: 'manual-search', params: deepState.manualParamsFromSearch },
    { name: 'urlsearch-window', params: Object.fromEntries(deepState.urlSearchParamsFromWindow.entries()) },
    { name: 'urlsearch-document', params: Object.fromEntries(deepState.urlSearchParamsFromDocument.entries()) },
  ];

  for (const source of sources) {
    const sessionId = source.params.sessionId || null;
    const playerId = source.params.playerId || null;

    if (sessionId) {
      // Extract game code from path
      const gameCodeMatch = deepState.windowLocationPathname.match(/\/join-game\/([A-Z0-9]+)/);
      const gameCode = gameCodeMatch ? gameCodeMatch[1] : null;

      logger.debug(`Found URL parameters from ${source.name}`, { sessionId, playerId, gameCode });
      return { sessionId, playerId, gameCode, source: source.name };
    }
  }

  // No parameters found
  return { sessionId: null, playerId: null, gameCode: null, source: 'none' };
};

/**
 * Monitor URL changes in real-time
 */
export class UrlMonitor {
  private listeners: Array<(state: DeepUrlState) => void> = [];
  private lastState: DeepUrlState | null = null;
  private intervalId: number | null = null;

  start(intervalMs: number = 500) {
    logger.debug('Starting URL monitor');

    this.intervalId = window.setInterval(() => {
      const currentState = getDeepUrlState();

      // Check if URL has changed
      if (!this.lastState ||
          this.lastState.windowLocationHref !== currentState.windowLocationHref ||
          this.lastState.windowLocationSearch !== currentState.windowLocationSearch) {

        logger.debug('URL change detected', {
          old: this.lastState?.windowLocationHref || 'none',
          new: currentState.windowLocationHref,
          oldSearch: this.lastState?.windowLocationSearch || 'none',
          newSearch: currentState.windowLocationSearch
        });

        this.lastState = currentState;
        this.notifyListeners(currentState);
      }
    }, intervalMs);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      logger.debug('URL monitor stopped');
    }
  }

  addListener(listener: (state: DeepUrlState) => void) {
    this.listeners.push(listener);
  }

  removeListener(listener: (state: DeepUrlState) => void) {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  private notifyListeners(state: DeepUrlState) {
    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('🚨 URL monitor listener error:', error);
      }
    });
  }

  getCurrentState(): DeepUrlState | null {
    return this.lastState;
  }
}

// Global URL monitor instance
export const urlMonitor = new UrlMonitor();

/**
 * Log comprehensive URL diagnostics
 */
export const logUrlDiagnostics = (context: string = 'Unknown') => {
  const deepState = getDeepUrlState();
  const foundParams = findParametersFromAllSources(deepState);

  logger.group(`Deep URL Diagnostics [${context}]`, () => {
    logger.debug('URLs', {
      window: deepState.windowLocationHref,
      document: deepState.documentLocationHref,
      search_window: deepState.windowLocationSearch,
      search_document: deepState.documentLocationSearch
    });

    logger.debug('Parameters Found', foundParams);

    logger.debug('Manual Extraction', {
      fromHref: deepState.manualParamsFromHref,
      fromSearch: deepState.manualParamsFromSearch
    });

    logger.debug('URLSearchParams', {
      fromWindow: Object.fromEntries(deepState.urlSearchParamsFromWindow.entries()),
      fromDocument: Object.fromEntries(deepState.urlSearchParamsFromDocument.entries())
    });

    logger.debug('History', {
      length: deepState.historyLength,
      state: deepState.historyState
    });
  });

  return { deepState, foundParams };
};
